import { gallerySlug, type GalleryPreviewData, madeWith } from '@shared/gallery'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Info } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { Button } from '@/components/Button'
import { BoardClient } from '@/board/client'
import { SessionsClient } from '@/session/store'
import { useI18n } from '@/i18n/i18n'
import { api } from '@/lib/api'
import { BoardWorkspace, type Clients } from './Board'

/** Offline clients over an example: the real board page, read only. */
function usePreviewClients(id: string, data: GalleryPreviewData | undefined): Clients | null {
  const [clients, setClients] = useState<Clients | null>(null)
  useEffect(() => {
    if (!data) return
    const board = new BoardClient(id, data)
    const sessions = new SessionsClient(board, id)
    // Like the board page: clients are made and torn down by an effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setClients({ board, sessions })
    return () => {
      sessions.destroy()
      board.destroy()
      setClients(null)
    }
  }, [id, data])
  return clients
}

/**
 * An example from the gallery: its board and the conversation that made it,
 * as they really look, read only. Copying makes it yours, conversation and
 * all, ready to continue.
 */
export function GalleryPreview() {
  const { slug = '' } = useParams()
  const { t } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const entry = gallerySlug(slug)
  const preview = useQuery({
    queryKey: ['gallery', slug],
    queryFn: () => api.gallery.preview(slug),
    enabled: !!entry,
    staleTime: Infinity,
  })
  const clients = usePreviewClients(`gallery-${slug}`, preview.data)
  const copy = useMutation({
    mutationFn: () => api.gallery.copy(slug),
    onSuccess: (board) => {
      void queryClient.invalidateQueries({ queryKey: ['boards'] })
      void navigate(`/b/${board.id}`)
    },
  })
  if (!entry) return <Navigate to="/" replace />
  return (
    <BoardWorkspace
      id={`gallery-${slug}`}
      title={entry.title}
      clients={clients}
      readOnly
      failed={preview.isError}
      headerExtra={
        <>
          <span className="hidden text-[12px] text-muted sm:inline">
            {t('gallery.readOnly')} · {madeWith(entry.made)}
          </span>
          <Button
            variant="primary"
            size="sm"
            aria-label={t('gallery.copy')}
            disabled={copy.isPending || !clients}
            onClick={() => {
              copy.mutate()
            }}
            className="flex-none"
          >
            <Copy size={14} />
            <span className="hidden sm:inline">{t('gallery.copy')}</span>
            <span className="sm:hidden">{t('gallery.copyShort')}</span>
          </Button>
        </>
      }
      notice={
        entry.medical ? (
          <p className="flex items-center gap-1.5 text-[12px] text-muted">
            <Info size={13} className="flex-none" />
            {t('gallery.medical')}
          </p>
        ) : null
      }
    />
  )
}
